import { useMemo } from 'react';
import { useQuery } from 'urql';

import { REFERENCE_LISTS_QUERY, TaxonomyType } from '@revelio/data-access';
import {
  SelectionCategories,
  type SelectionList,
  type SelectionListIdNames,
} from '@revelio/iso-utility';

import { RICS_TAXONOMY_ID } from '../../../../shared/selection-lists/constants';
import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useGeographyTaxonomyTree } from '../../../../shared/selection-lists/hooks/useGeographyTaxonomyTree';
import { useRoleTreeSelectionLists } from '../../../../shared/selection-lists/hooks/useRoleTreeSelectionLists';
import { useTaxonomyTree } from '../../../../shared/selection-lists/hooks/useTaxonomyTree';
import type {
  AllSelectionListsWithSkillTaxonomyKey,
  RoleSelectionListKey,
} from '../../../../shared/selection-lists/types';
import { normalizeReferenceLists } from '../../../../shared/selection-lists/utils/referenceLists';
import { treeToGeoSelectionLists } from '../../../../shared/selection-lists/utils/treeToGeoSelectionLists';
import {
  type ParentLinkedItem,
  treeToParentLinkedSelectionLists,
} from '../../../../shared/selection-lists/utils/treeToParentLinkedSelectionLists';
import { useRoleTaxonomySelection } from '../../../../shared/user-preferences/hooks/useRoleTaxonomySelection';
import { useSkillTaxonomy } from '../../../../shared/user-preferences/hooks/useSkillTaxonomySelection';
import { ALL_STATIC_SELECTION_LISTS } from '../utils/staticSelectionLists';
import { useGeographyTaxonomy } from './useGeographyTaxonomy';

// Full k10→k50→k400→company so rics_k400/company items keep their k10 + k50
// ancestry FKs (homogenizeRicsSelections / composition rely on them).
const RICS_TREE_LEVELS = [1, 2, 3, 4];

const pickLists = (
  source: Partial<Record<string, ParentLinkedItem[]>>,
  keys: AllSelectionListsWithSkillTaxonomyKey[]
): Partial<Record<string, ParentLinkedItem[]>> => {
  const picked: Partial<Record<string, ParentLinkedItem[]>> = {};
  for (const key of keys) {
    const list = source[key];
    if (list !== undefined) picked[key] = list;
  }
  return picked;
};
const RICS_SELECTION_LIST_KEYS: AllSelectionListsWithSkillTaxonomyKey[] = [
  'rics_k10',
  'rics_k400',
  'company',
];

// The role levels the breadcrumb picker exposes (role_k7 → role_k150 →
// role_k1500 → role_k15000); sourced from the role TaxonomyTree.
const ALLOWED_ROLE_KEYS: RoleSelectionListKey[] = [
  'role_k7',
  'role_k150',
  'role_k1500',
  'role_k15000',
];

export const useAllSelectionLists = (): SelectionList[] => {
  const roleTaxonomySelection = useRoleTaxonomySelection();
  const { customSkillTree } = useCustomSkillTaxonomyTree();
  // The taxonomy the custom skill tree above is keyed by — used to decide
  // whether the DEFAULT-taxonomy API skill lists are a valid stand-in while
  // that tree loads (they are only when this IS the default taxonomy).
  const skillTaxonomyId = useSkillTaxonomy();

  const { treeData: ricsTreeData, levels: ricsLevels } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Rics,
    taxonomyId: RICS_TAXONOMY_ID,
    levels: RICS_TREE_LEVELS,
    // Same RICS cache key as the adaptive resolution's request — tag it high
    // here too so whichever consumer urql dedupes to keeps the tree on the
    // composition's critical path rather than in the low-priority picker batch.
    priority: 'high',
  });

  // The picker's geography lists must come from the taxonomy the user is
  // actually on, not a hardcoded metro id — otherwise a state/county selection
  // has no list to resolve its label from.
  const { taxonomyId: geoTaxonomyId, selectionListIds: geoSelectionListKeys } =
    useGeographyTaxonomy();

  const { treeData: geoTreeData, levels: geoLevels } = useGeographyTaxonomyTree(
    {
      taxonomyId: geoTaxonomyId,
    }
  );

  const [{ data: referenceListsData }] = useQuery({
    query: REFERENCE_LISTS_QUERY,
  });

  const roleSelectionLists = useRoleTreeSelectionLists({
    requiredKeys: ALLOWED_ROLE_KEYS,
    taxonomySelection: roleTaxonomySelection,
  });

  const allSelectionLists = useMemo(() => {
    const ricsLists = pickLists(
      treeToParentLinkedSelectionLists(ricsTreeData, ricsLevels),
      RICS_SELECTION_LIST_KEYS
    );
    const geoLists = pickLists(
      treeToGeoSelectionLists(geoTreeData, geoLevels),
      geoSelectionListKeys as AllSelectionListsWithSkillTaxonomyKey[]
    );

    const referenceLists = normalizeReferenceLists(referenceListsData);

    const apiSelectionLists = {
      ...ricsLists,
      ...geoLists,
      ...(referenceLists ?? {}),
    };

    const apiSelectionListItems = Object.entries(apiSelectionLists).map(
      ([id, list]) => ({
        id,
        value: list,
      })
    );

    // Build custom skill selection lists (skill_k75 -> L0, skill_k700 -> L1, skill_k3000 -> L2, skill_k15000 -> L3)
    const customSkillSelectionListItems: SelectionList[] = (() => {
      if (!customSkillTree || customSkillTree.length === 0) return [];

      const level0 = customSkillTree.map((n) => ({
        id: n.id,
        longName: n.name,
      }));

      const level1 = customSkillTree.flatMap((n0) =>
        (n0.children || []).map((n1) => {
          const parts = String(n1.id).split('_');
          const id = parts[parts.length - 1];
          return { id, longName: n1.name, skill_0: n0.id };
        })
      );

      const level2 = customSkillTree.flatMap((n0) =>
        (n0.children || []).flatMap((n1) =>
          (n1.children || []).map((n2) => {
            const parts = String(n2.id).split('_');
            const id = parts[parts.length - 1];
            const parentParts = String(n1.id).split('_');
            const parentId = parentParts[parentParts.length - 1];
            return { id, longName: n2.name, skill_1: parentId };
          })
        )
      );

      const level3 = customSkillTree.flatMap((n0) =>
        (n0.children || []).flatMap((n1) =>
          (n1.children || []).flatMap((n2) =>
            (n2.children || []).map((n3) => {
              const parts = String(n3.id).split('_');
              const id = parts[parts.length - 1];
              const parentParts = String(n2.id).split('_');
              const parentId = parentParts[parentParts.length - 1];
              return { id, longName: n3.name, skill_2: parentId };
            })
          )
        )
      );

      return [
        {
          id: SelectionCategories.SKILL_0,
          value: level0,
        },
        {
          id: SelectionCategories.SKILL_1,
          value: level1,
          parent: 'skill_0',
        },
        {
          id: SelectionCategories.SKILL_2,
          value: level2,
          parent: 'skill_1',
        },
        {
          id: SelectionCategories.SKILL_3,
          value: level3,
          parent: 'skill_2',
        },
      ] as SelectionList[];
    })();

    const roleSelectionListItems = Object.entries(roleSelectionLists ?? {})
      .filter(([id]) => ALLOWED_ROLE_KEYS.includes(id as RoleSelectionListKey))
      .map(([id, list]) => ({
        id,
        value: list,
      }));

    // If custom skills are available, replace API skill lists with custom
    // ones. When the ACTIVE skill taxonomy is non-default, drop the API skill
    // lists even while the custom tree is still loading: they carry the
    // DEFAULT taxonomy's labels, and skill ids are taxonomy-relative — a
    // label stamped from them during the load window looks valid and is never
    // re-patched. With no list exposed, the name patcher waits and the names
    // self-heal when the correct tree lands (same contract as ROLE).
    const skillIds = new Set<SelectionList['id']>([
      SelectionCategories.SKILL_0,
      SelectionCategories.SKILL_1,
      SelectionCategories.SKILL_2,
      SelectionCategories.SKILL_3,
    ]);

    const dropApiSkillLists =
      customSkillSelectionListItems.length > 0 ||
      (skillTaxonomyId !== undefined && skillTaxonomyId !== 0);
    const apiMinusSkills = dropApiSkillLists
      ? apiSelectionListItems.filter(
          (item) => !skillIds.has(item?.id as SelectionListIdNames)
        )
      : apiSelectionListItems;

    return [
      ...apiMinusSkills,
      ...(customSkillSelectionListItems.length
        ? customSkillSelectionListItems
        : []),
      ...roleSelectionListItems,
      ...ALL_STATIC_SELECTION_LISTS,
    ] as SelectionList[];
  }, [
    ricsTreeData,
    ricsLevels,
    geoTreeData,
    geoLevels,
    geoSelectionListKeys,
    referenceListsData,
    roleSelectionLists,
    customSkillTree,
    skillTaxonomyId,
  ]);

  return allSelectionLists;
};
